import type { CanvasFeature } from '@pn/core/domain/drawing';
import type { Point } from '@pn/core/domain/point';
import { isTransparent } from '@pn/services/utils/color';
import { round } from 'lodash-es';

export function pathToPoints(pathData: string, decimals = 1): Point[] {
  const commands = pathData.match(/[a-zA-Z][^a-zA-Z]*/g);
  if (!commands) return [];

  const points: Point[] = [];

  for (const command of commands) {
    const type = command[0];
    const coords = command
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map((n) => round(Number(n), decimals));

    switch (type) {
      case 'M':
      case 'L':
        for (let i = 0; i < coords.length; i += 2) {
          points.push({ x: coords[i], y: coords[i + 1] });
        }
        break;
      case 'C':
        for (let i = 4; i < coords.length; i += 6) {
          points.push({ x: coords[i], y: coords[i + 1] });
        }
        break;
      case 'Q':
        for (let i = 2; i < coords.length; i += 4) {
          points.push({ x: coords[i], y: coords[i + 1] });
        }
        break;
      case 'S':
      case 'T':
        for (let i = 2; i < coords.length; i += 4) {
          points.push({ x: coords[i], y: coords[i + 1] });
        }
        break;
      default:
        console.error(`Unsupported path command: ${type}`);
        break;
    }
  }

  return points;
}

export function pointsToCurvedPath(points: Point[]): string {
  if (points.length === 1)
    return `M${points[0].x} ${points[0].y} L${points[0].x} ${points[0].y}`;
  if (points.length === 2)
    return `M${points[0].x} ${points[0].y} L${points[1].x} ${points[1].y}`;

  const pathCommands = points.slice(0, -1).map((start, i) => {
    const end = points[i + 1];
    const isFirst = i === 0;
    const isLast = i === points.length - 2;

    /* The ends of an open curve get no tangent from outside the curve */
    const before = isFirst ? start : points[i - 1];
    const after = isLast ? end : points[i + 2];

    const firstControl = isFirst
      ? start
      : getControlPoint(before, start, end, 'start');
    const secondControl = isLast
      ? end
      : getControlPoint(start, end, after, 'end');

    const curve = `C${firstControl.x} ${firstControl.y},${secondControl.x} ${secondControl.y},${end.x} ${end.y}`;

    return isFirst ? `M${start.x} ${start.y} ${curve}` : curve;
  });

  return pathCommands.join(' ');
}

/**
 * Control point at vertex `b` of `a`–`b`–`c`, by Maxim Shemanarev's method
 * ("Interpolation with Bezier Curves") with full smoothing. The tangent at `b`
 * is parallel to `a`–`c`, and the control point is placed on the `c` side
 * (`'start'` of segment `b`–`c`) or the `a` side (`'end'` of segment `a`–`b`).
 */
function getControlPoint(
  a: Point,
  b: Point,
  c: Point,
  side: 'start' | 'end'
): Point {
  const abLength = Math.hypot(b.x - a.x, b.y - a.y);
  const bcLength = Math.hypot(c.x - b.x, c.y - b.y);
  const totalLength = abLength + bcLength;
  const k = totalLength === 0 ? 0 : abLength / totalLength;

  /* Point between the two segment midpoints, split by segment length */
  const pivot = {
    x: (a.x + b.x) / 2 + ((c.x - a.x) / 2) * k,
    y: (a.y + b.y) / 2 + ((c.y - a.y) / 2) * k,
  };
  const neighbour = side === 'start' ? c : a;

  return {
    x: b.x + (b.x + neighbour.x) / 2 - pivot.x,
    y: b.y + (b.y + neighbour.y) / 2 - pivot.y,
  };
}

export function isFeaturePathHovered(params: {
  ctx: CanvasRenderingContext2D;
  feature: CanvasFeature;
  path: Path2D;
  point: Point;
}): boolean {
  const {
    ctx,
    feature,
    path,
    point: { x, y },
  } = params;

  if ('strokeWidth' in feature) ctx.lineWidth = feature.strokeWidth;

  const isInPath = () =>
    shouldMatchPath(feature) && ctx.isPointInPath(path, x, y);
  const isInStroke = () => ctx.isPointInStroke(path, x, y);

  switch (feature.type) {
    case 'path':
      return isInStroke();
    case 'poly':
    case 'circle':
      return isInPath() || isInStroke();
    case 'text':
    case 'icon':
      return isInPath();
  }
}

function shouldMatchPath(feature: CanvasFeature): boolean {
  return (
    ['text', 'icon'].includes(feature.type) ||
    ('fillColor' in feature && !isTransparent(feature.fillColor))
  );
}
